(() => {
  class PromoSplit {
    constructor(root) {
      this.root = root;
      this.slides = [...root.querySelectorAll('[data-feature-slide]')];
      this.dots = [...root.querySelectorAll('[data-feature-dot]')];
      this.status = root.querySelector('[data-feature-status]');
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.delay = Number(root.dataset.autoplayDelay) || 6000;
      this.autoplayEnabled = root.dataset.autoplay === 'true';
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.videoInView = false;
      this.controller = new AbortController();
      this.timer = 0;
      this.pauses = new Set();
      if (root.querySelector('[data-feature-video]')) this.pauses.add('offscreen');
      root.style.setProperty('--promo-progress-duration', `${Number(root.dataset.progressDelay) || 7000}ms`);
      root.dataset.promoInitialized = 'true';
      this.bind();
      this.observe();
      this.activate(this.index, false);
      this.schedule();
    }

    bind() {
      const signal = this.controller.signal;
      this.root.querySelector('[data-feature-prev]')?.addEventListener('click', () => this.activate(this.index - 1, true), { signal });
      this.root.querySelector('[data-feature-next]')?.addEventListener('click', () => this.activate(this.index + 1, true), { signal });
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.activate(Number(dot.dataset.index), true), { signal }));
      this.root.addEventListener('pointerenter', () => this.pause('hover'), { signal });
      this.root.addEventListener('pointerleave', () => this.resume('hover'), { signal });
      this.root.addEventListener('focusin', () => this.pause('focus'), { signal });
      this.root.addEventListener('focusout', (event) => {
        if (!this.root.contains(event.relatedTarget)) this.resume('focus');
      }, { signal });
      document.addEventListener('visibilitychange', () => {
        this.updateVideos();
        if (document.hidden) this.pause('hidden');
        else this.resume('hidden');
      }, { signal });
      this.reduceMotion.addEventListener('change', () => {
        this.updateVideos();
        if (this.reduceMotion.matches) this.stop();
        else this.schedule();
      }, { signal });
    }

    observe() {
      if (this.root.querySelector('[data-feature-video]')) {
        this.observer = new IntersectionObserver(([entry]) => {
          this.videoInView = entry.isIntersecting && entry.intersectionRatio >= 0.1;
          if (this.videoInView) this.resume('offscreen');
          else this.pause('offscreen');
          this.updateVideos();
        }, { threshold: 0.1 });
        this.observer.observe(this.root.querySelector('[data-feature-carousel]'));
      }
      this.root.classList.add('is-in-view');
      this.slides[this.index]?.classList.add('is-revealed');
    }

    activate(nextIndex, userInitiated) {
      if (!this.slides.length) return;
      const target = (nextIndex + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, index) => {
        const active = index === target;
        slide.classList.toggle('is-active', active);
        slide.classList.toggle('is-revealed', active && this.root.classList.contains('is-in-view'));
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        if ('inert' in slide) slide.inert = !active;
      });
      this.dots.forEach((dot, index) => {
        const active = index === target;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
        const progress = dot.querySelector('span');
        if (active && progress) {
          progress.style.animation = 'none';
          progress.offsetWidth;
          progress.style.animation = '';
        }
      });
      this.index = target;
      this.updateVideos();
      if (userInitiated && this.status) this.status.textContent = `${target + 1} of ${this.slides.length}`;
      this.stop();
      this.schedule();
    }

    updateVideos() {
      this.slides.forEach((slide, index) => {
        const video = slide.querySelector('[data-feature-video]');
        if (!video) return;
        if (index === this.index && this.videoInView && !this.reduceMotion.matches && !document.hidden) video.play().catch(() => {});
        else video.pause();
      });
    }

    schedule() {
      if (!this.autoplayEnabled || this.reduceMotion.matches || this.pauses.size || this.slides.length < 2) return;
      this.stop();
      this.timer = window.setTimeout(() => this.activate(this.index + 1, false), this.delay);
    }

    stop() {
      if (this.timer) window.clearTimeout(this.timer);
      this.timer = 0;
    }

    pause(reason) {
      this.pauses.add(reason);
      this.root.classList.add('is-paused');
      this.stop();
    }

    resume(reason) {
      this.pauses.delete(reason);
      this.root.classList.toggle('is-paused', this.pauses.size > 0);
      this.schedule();
    }

    selectBlock(block) {
      const slide = block.closest('[data-feature-slide]');
      if (slide) this.activate(Number(slide.dataset.index), false);
      this.pause('editor');
    }

    destroy() {
      this.root.querySelectorAll('[data-feature-video]').forEach((video) => video.pause());
      this.stop();
      this.observer?.disconnect();
      this.controller.abort();
      delete this.root.dataset.promoInitialized;
    }
  }

  const instances = new Map();
  const init = (scope = document) => {
    scope.querySelectorAll('[data-promo-split]').forEach((root) => {
      if (root.dataset.promoInitialized === 'true') return;
      instances.set(root.id, new PromoSplit(root));
    });
  };

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-promo-split]');
    if (!root) return;
    instances.get(root.id)?.destroy();
    instances.delete(root.id);
  });
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest('[data-promo-split]');
    instances.get(root?.id)?.selectBlock(event.target);
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    const root = event.target.closest('[data-promo-split]');
    instances.get(root?.id)?.resume('editor');
  });
})();
